import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Warning, ShieldWarning } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_REASONS = [
  { value: 'harassment', label: 'Harassment or bullying', desc: 'Targeted attacks, threats, or hate speech' },
  { value: 'spam', label: 'Spam or scam', desc: 'Unwanted promotions, phishing, or fake content' },
  { value: 'inappropriate', label: 'Inappropriate content', desc: 'Sexual, violent, or otherwise unsafe content' },
  { value: 'safety', label: 'Safety concern', desc: 'I feel unsafe or believe someone is at risk' },
  { value: 'scam', label: 'Scam or fraud', desc: 'Deceptive event, fake host, or financial scam' },
  { value: 'other', label: 'Something else', desc: 'Describe the issue below' },
];

/**
 * Shared report modal used by ProfilePage, EventDetailsPage, etc.
 * Apple App Store Guideline 1.2 requires a frictionless content/user reporting flow on any
 * app with user-generated content.
 */
export default function ReportModal({ open, onClose, entityType, entityId, entityLabel }) {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    if (submitting) return;
    setReason('');
    setDescription('');
    onClose();
  };

  const submit = async () => {
    if (!reason) {
      toast.error('Pick a reason first');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/moderation/reports`,
        { entity_type: entityType, entity_id: entityId, reason, description: description.trim() },
        { withCredentials: true }
      );
      toast.success('Report submitted — our team will review it within 24 hours');
      close();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && close()}
          data-testid="report-modal"
        >
          <motion.div
            initial={{ y: 100, scale: 0.95 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 100, scale: 0.95 }}
            className="bg-[#0e0e16] border border-white/[0.06] rounded-2xl w-full max-w-md overflow-hidden"
          >
            <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Warning size={18} weight="duotone" className="text-[#FACC15]" />
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Report</h3>
              </div>
              <button onClick={close} className="text-[#666] hover:text-white" data-testid="report-modal-close">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {entityLabel && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-[#0a0a12] border border-white/[0.04]">
                  <ShieldWarning size={14} className="text-[#71717A] mt-0.5 shrink-0" />
                  <p className="text-[#A1A1AA] text-xs">
                    Reporting: <span className="text-white font-medium">{entityLabel}</span>
                  </p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-[#A1A1AA] mb-2">Why are you reporting this?</label>
                <div className="space-y-1.5">
                  {REPORT_REASONS.map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setReason(r.value)}
                      className={`w-full text-left p-2.5 rounded-lg border transition-all ${
                        reason === r.value
                          ? 'bg-[#9D4CDD]/15 border-[#9D4CDD]/50'
                          : 'bg-[#0a0a12] border-white/[0.04] hover:border-white/10'
                      }`}
                      data-testid={`report-reason-${r.value}`}
                    >
                      <p className="text-white text-sm font-medium">{r.label}</p>
                      <p className="text-[#71717A] text-[10px] mt-0.5">{r.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#A1A1AA] mb-2">
                  Additional details {reason === 'other' ? '*' : '(optional)'}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  placeholder="Tell us what happened…"
                  className="input-field w-full text-sm resize-none"
                  data-testid="report-description-input"
                />
                <p className="text-[10px] text-[#71717A] text-right">{description.length}/1000</p>
              </div>
            </div>

            <div className="p-5 pt-2 flex gap-2 border-t border-white/[0.04]">
              <button onClick={close} className="btn-secondary flex-1" data-testid="report-cancel">
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={submitting || !reason || (reason === 'other' && !description.trim())}
                className="btn-primary flex-1"
                data-testid="report-submit"
              >
                {submitting ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
